import { Label, Modal, Spinner } from "flowbite-react"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { LeftSmallIcon } from "@assets/icons"
import CustomImage from "@components/elements/CustomImage"
import useAuth from "@hooks/useAuth"
import { useMediaQuery } from "@hooks/useMediaQuery"
import BookingSwLogo from "@icons/BookingSwLogo"
import CheckoutModal from "@modules/widgets/components/CheckoutModal"
import { domainPlaceholder, widgetsDomainName } from "@mono/essentials/constants/options"
import { ProductInternalTypeEnum } from "@mono/essentials/constants/options"
import { useGetCategoryListWithProducts } from "@mono/essentials/entities/cart/hooks"
import {
  useGetActiveProducts,
  useGetActiveProductsWithCustomerLoggedIn,
  useGetActiveProductsForMembership,
  useGetProductInfoById,
} from "@mono/essentials/hooks/WidgetHooks"
import {
  ProductCategoryType,
  ProductType,
  GetOrderDetailsResponseType,
} from "@mono/essentials/types/OrderTypes"

import CustomerLogin from "../CustomerLogin"
import OrderConfirmModal from "../OrderConfirmModal"
import CategoryPreview from "../Preview/CategoryPreview"
import CategoryProductPreview from "../Preview/CategoryProductPreview"
import ProductBuyModal from "../ProductBuyModal"
import ProductGiftCardBuyModal from "../ProductGiftCardBuyModal"
import ProductMembershipBuyModal from "../ProductMembershipBuyModal"
import ProductMerchandizeBuyModal from "../ProductMerchandizeBuyModal"
import ProductRentalBuyModal from "../ProductRentalBuyModal"

interface ISelectProductModalProps {
  show: boolean
  setShow: (show: boolean) => void
  productDetails: {
    show_specific_product_detail?: boolean
    show_select_product?: boolean
    product_id?: string
    category_id?: string
    widget_type?: string
    product_description_view?: string
  }
  isFromCheckOutModal: boolean
  refetchCartData: () => void
  isIntegratedPage: boolean
  handleClose: (isCancel?: boolean) => void
  cartItemDetails: Record<string, unknown>
  cartParticipantsDetails: Record<string, unknown>
  productInfo: Record<string, unknown>
  cart_Id: string
  customer_id: string
  isSelectedDate: Date | null
  locationProducts: ProductCategoryType[]
  productCategoryData: ProductCategoryType[]
  isLoading: boolean
  isSelectedProductDetailValidating: boolean
  isCustomWidget: boolean
  refetchParticipantsDetails: () => void
  orderSummary: Record<string, unknown>
  isFromPagePreview?: boolean
  hideStepper?: boolean
  lastStepCount?: number
  onProductDetailsPreviewSubmit?: () => void
  hideQuantityEdit?: boolean
  hideCustomerDetails?: boolean
  isMemberModal?: boolean
}

const SelectProductModal = ({
  show,
  setShow,
  productDetails,
  isFromCheckOutModal,
  refetchCartData,
  isIntegratedPage,
  handleClose,
  cartItemDetails,
  cartParticipantsDetails,
  productInfo,
  cart_Id,
  customer_id,
  isSelectedDate,
  locationProducts,
  productCategoryData,
  isLoading,
  isSelectedProductDetailValidating,
  isCustomWidget,
  refetchParticipantsDetails,
  orderSummary,
  isFromPagePreview = false,
  hideStepper = false,
  lastStepCount,
  onProductDetailsPreviewSubmit,
  hideQuantityEdit = false,
  hideCustomerDetails = false,
  isMemberModal = false,
}: ISelectProductModalProps) => {
  const [selectedProductCategory, setSelectedProductCategory] = useState<ProductCategoryType>()
  const [selectedProduct, setSelectedProduct] = useState<ProductType>()
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [showCheckoutModal, setShowCheckOutModal] = useState<boolean>(false)
  const [cartId, setcartId] = useState<string>("")
  const [orderData, setOrderData] = useState<GetOrderDetailsResponseType | undefined>(undefined)
  const [orderConfirmModal, setOrderConfirmModal] = useState(false)

  const modalHeading = useMemo(() => {
    switch (currentStep) {
      case 1:
        return "All Categories"
      case 2:
        return "Select Product"
      default:
        return ""
    }
  }, [currentStep])

  const router = useRouter()
  const isMobile = useMediaQuery(375)
  const { publicDomainDetails: domainDetails, loggedInCustomerDetails } = useAuth()

  const enableAPI = useMemo(() => {
    return (
      !productCategoryData &&
      !isLoading &&
      !productDetails?.product_id &&
      show &&
      router.isReady &&
      productDetails?.widget_type !== "location_view"
    )
  }, [
    productCategoryData,
    isLoading,
    productDetails?.product_id,
    show,
    router.isReady,
    productDetails?.widget_type,
  ])

  const resetData = () => {
    setSelectedProductCategory(undefined)
    setSelectedProduct(undefined)
  }

  useEffect(() => {
    if (productDetails) {
      if (productDetails.show_specific_product_detail && productDetails.product_id !== "") {
        setCurrentStep(3)
      } else if (productDetails.show_select_product && productDetails.category_id !== "") {
        setCurrentStep(2)
      } else {
        setCurrentStep(1)
        resetData()
      }
    }
  }, [show])

  const { data: productInfoById, isLoading: isProductInfoLoader } = useGetProductInfoById({
    productId: productDetails?.product_id || "",
    isIntegratedPage,
    queryOptions: {
      enabled: Boolean(show && productDetails?.product_id),
    },
  })

  const {
    data: categoryListWithProductsForAll,
    isLoading: isCategoryListWithProductsForAllLoading,
  } = useGetCategoryListWithProducts({
    isIntegratedPage,
    queryOptions: {
      enabled: Boolean(enableAPI && !loggedInCustomerDetails?.id && !isMemberModal),
    },
  })

  const {
    data: categoryListWithProductsForCustomerLoggedIn,
    isLoading: isCategoryListWithProductsForCustomerLoggedInLoading,
  } = useGetCategoryListWithProducts({
    customerId: loggedInCustomerDetails?.id,
    isIntegratedPage,
    queryOptions: {
      enabled: Boolean(enableAPI && loggedInCustomerDetails?.id),
    },
  })

  const {
    data: categoryListWithProductsForMembership,
    isLoading: isCategoryListWithProductsForMembershipLoading,
  } = useGetActiveProductsForMembership({
    isIntegratedPage,
    queryOptions: {
      enabled: Boolean(enableAPI && isMemberModal),
    },
  })

  const categoryListWithProducts = useMemo(() => {
    if (isMemberModal) {
      return categoryListWithProductsForMembership
    } else if (loggedInCustomerDetails?.id) {
      return categoryListWithProductsForCustomerLoggedIn
    }
    return categoryListWithProductsForAll
  }, [
    categoryListWithProductsForAll,
    categoryListWithProductsForCustomerLoggedIn,
    categoryListWithProductsForMembership,
    isMemberModal,
    loggedInCustomerDetails,
  ])

  const isCategoryListWithProductsLoading = useMemo(() => {
    if (isMemberModal) {
      return isCategoryListWithProductsForMembershipLoading
    } else if (loggedInCustomerDetails?.id) {
      return isCategoryListWithProductsForCustomerLoggedInLoading
    }
    return isCategoryListWithProductsForAllLoading
  }, [
    isCategoryListWithProductsForAllLoading,
    isCategoryListWithProductsForCustomerLoggedInLoading,
    isCategoryListWithProductsForMembershipLoading,
    isMemberModal,
    loggedInCustomerDetails,
  ])

  useEffect(() => {
    if (productInfoById && !selectedProduct) {
      setSelectedProduct(productInfoById)
    }
  }, [productInfoById, selectedProduct])

  const handleModalClose = (isCancel = false) => {
    setShow(false)
    setCurrentStep(1)
    resetData()
    handleClose(isCancel)
    setSelectedProduct(undefined)
  }

  const handleBackButton = () => {
    setCurrentStep(currentStep - 1)
    resetData()
  }

  const allCategoryList =
    productDetails?.widget_type === "location_view"
      ? locationProducts
      : productCategoryData || categoryListWithProducts

  const activeProductCategoryList = useMemo(() => {
    return allCategoryList?.filter((productCategory) => {
      const showMembershipProducts = isFromCheckOutModal
        ? (
            productCategory.activeProducts?.filter(
              (item) => item.type.internal_type !== (ProductInternalTypeEnum.MEMBERSHIP as string)
            ) || []
          ).length
        : true
      return productCategory.activeProducts.length && showMembershipProducts
    })
  }, [allCategoryList, isFromCheckOutModal])

  useEffect(() => {
    if (
      activeProductCategoryList?.length === 1 &&
      activeProductCategoryList[0].activeProducts &&
      activeProductCategoryList[0].activeProducts.length === 1 &&
      activeProductCategoryList[0].activeProducts[0].type.internal_type ===
        (ProductInternalTypeEnum.MEMBERSHIP as string) &&
      !selectedProduct
    ) {
      setSelectedProduct(activeProductCategoryList[0].activeProducts[0])
      setCurrentStep(3)
    }
  }, [activeProductCategoryList, selectedProduct])

  const goToBackStep = useCallback(
    (isResetToFirstStep = false) => {
      if (
        currentStep === 3 &&
        activeProductCategoryList?.length === 1 &&
        activeProductCategoryList[0].activeProducts &&
        activeProductCategoryList[0].activeProducts.length === 1
      ) {
        handleModalClose(true)
      } else if (isResetToFirstStep) {
        setCurrentStep((prev) => (prev - 2 > 0 ? prev - 2 : prev))
      } else {
        setCurrentStep((prev) => (prev - 1 > 0 ? prev - 1 : prev))
      }
    },
    [currentStep, activeProductCategoryList, handleModalClose]
  )

  const getModalBody = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={"py-5 h-full overflow-auto sm:px-8 px-4 pt-6 bg-whiteBlack-100"}>
            {isLoading || isCategoryListWithProductsLoading || !domainDetails ? (
              <div className={"min-h-[200px] flex items-center justify-center"}>
                <Spinner size={"md"} className={"text-primary-400 fill-white"} />
              </div>
            ) : (
              <>
                {activeProductCategoryList?.length ? (
                  <div className={"grid gap-3 lg:grid-cols-3 sm:grid-cols-2 sm:gap-6"}>
                    <CategoryPreview
                      categoryList={activeProductCategoryList}
                      setSelectedProductCategory={setSelectedProductCategory}
                      goToNextStep={(categoryId, firstProduct, info, hasSingleProduct) => {
                        if (hasSingleProduct) {
                          setSelectedProduct(firstProduct)
                        }
                        setCurrentStep(!hasSingleProduct ? 2 : 3)
                      }}
                      isModal
                    />
                  </div>
                ) : (
                  <div className={"min-h-[100px] flex items-center justify-center"}>
                    <Label className={"text-center text14Regular text-neutral-500"}>
                      Unfortunately, there are no active product categories found right now.
                    </Label>
                  </div>
                )}
              </>
            )}
          </div>
        )
      case 2:
        return selectedProductCategory || productDetails.category_id || !domainDetails ? (
          <div className={"h-full flex-1 overflow-auto sm:px-8 px-4 py-5 bg-whiteBlack-100"}>
            {isLoading || isCategoryListWithProductsLoading ? (
              <div className={"min-h-[200px] flex items-center justify-center"}>
                <Spinner size={"md"} className={"text-primary-400 fill-white"} />
              </div>
            ) : (
              <div
                className={
                  "grid gap-3 sm:grid-cols-2 min-[850px]:grid-cols-3  min-[768px]:grid-cols-2 sm:gap-5"
                }
              >
                <CategoryProductPreview
                  goToNextStep={() => setCurrentStep(3)}
                  setSelectedProduct={setSelectedProduct}
                  selectedCategory={
                    selectedProductCategory?.id || (productDetails?.category_id as string)
                  }
                  categoryList={activeProductCategoryList}
                  isModal
                />
              </div>
            )}
          </div>
        ) : null
      case 3:
        if (selectedProduct) {
          if (selectedProduct?.type.name === "Gift Card") {
            return (
              <ProductGiftCardBuyModal
                show={show}
                domainDetails={domainDetails}
                selectedProductData={selectedProduct}
                isSelectedProductDetailValidating={isSelectedProductDetailValidating}
                goToBackStep={goToBackStep}
                closeModal={handleModalClose}
                selectedProduct={selectedProduct?.id || (productDetails.product_id as string)}
                isIntegratedPage={isIntegratedPage}
                onSuccess={(cartId: string) => {
                  if (isFromCheckOutModal) {
                    refetchCartData()
                    handleModalClose()
                  } else {
                    setShowCheckOutModal(true)
                    setcartId(cartId)
                  }
                }}
                cartId={cart_Id}
                updateCart={Boolean(cart_Id)}
                cartItemDetails={cartItemDetails}
                cartParticipantsDetails={cartParticipantsDetails}
                isFromCheckOutModal={isFromCheckOutModal}
                customer_id={customer_id}
                productInitialValues={productInfo}
                isCustomWidget={isCustomWidget}
              />
            )
          } else if (selectedProduct?.is_dynamic_rental) {
            return (
              <ProductRentalBuyModal
                show={show}
                selectedProduct={selectedProduct?.id || (productDetails.product_id as string)}
                selectedProductData={selectedProduct}
                pagePreview={productDetails?.product_description_view || "default_view"}
                goToBackStep={goToBackStep}
                closeModal={handleModalClose}
                onSuccess={(cartId: string) => {
                  if (isFromCheckOutModal) {
                    refetchCartData()
                    handleModalClose()
                  } else {
                    setShowCheckOutModal(true)
                    setcartId(cartId)
                  }
                }}
                handleHomeButtonClick={handleModalClose}
                isIntegratedPage={isIntegratedPage}
                setCurrentStepTobacktStep={setCurrentStep}
                currentStepToBackStep={currentStep}
                domainDetails={domainDetails}
                productInitialValues={productInfo}
                cartId={cart_Id}
                updateCart={Boolean(cart_Id)}
                cartItemDetails={cartItemDetails}
                cartParticipantsDetails={cartParticipantsDetails}
                isFromCheckOutModal={isFromCheckOutModal}
                customer_id={customer_id}
                isSelectedDate={isSelectedDate}
                isCustomWidget={isCustomWidget}
                refetchParticipantsDetails={refetchParticipantsDetails}
                orderSummary={orderSummary}
                isFromPagePreview={isFromPagePreview}
                hideStepper={hideStepper}
                lastStepCount={lastStepCount}
                onSubmit={onProductDetailsPreviewSubmit}
                hideQuantityEdit={hideQuantityEdit}
                productDetails={productDetails}
              />
            )
          } else if (
            selectedProduct?.type?.internal_type === (ProductInternalTypeEnum.MERCHANDISE as string)
          ) {
            return (
              <ProductMerchandizeBuyModal
                show={show}
                domainDetails={domainDetails}
                selectedProductData={selectedProduct}
                isLoading={isProductInfoLoader}
                goToBackStep={goToBackStep}
                closeModal={handleModalClose}
                selectedProduct={selectedProduct?.id || (productDetails.product_id as string)}
                isIntegratedPage={isIntegratedPage}
                onSuccess={(cartId: string) => {
                  if (isFromCheckOutModal) {
                    refetchCartData()
                    handleModalClose()
                  } else {
                    setShowCheckOutModal(true)
                    setcartId(cartId)
                  }
                }}
                cartId={cart_Id}
                updateCart={Boolean(cart_Id)}
                cartItemDetails={cartItemDetails}
                cartParticipantsDetails={cartParticipantsDetails}
                isFromCheckOutModal={isFromCheckOutModal}
                customer_id={customer_id}
                productInitialValues={productInfo}
              />
            )
          } else if (
            selectedProduct?.type?.internal_type === (ProductInternalTypeEnum.MEMBERSHIP as string)
          ) {
            return (
              <ProductMembershipBuyModal
                show={show}
                domainDetails={domainDetails}
                selectedProductData={selectedProduct}
                isSelectedProductDetailValidating={isSelectedProductDetailValidating}
                goToBackStep={goToBackStep}
                closeModal={handleModalClose}
                selectedProduct={selectedProduct?.id || (productDetails.product_id as string)}
                isIntegratedPage={isIntegratedPage}
                onSuccess={(cartId: string) => {
                  if (isFromCheckOutModal) {
                    refetchCartData()
                    handleModalClose()
                  } else {
                    setShowCheckOutModal(true)
                    setcartId(cartId)
                  }
                }}
                cartId={cart_Id}
                updateCart={Boolean(cart_Id)}
                cartItemDetails={cartItemDetails}
                cartParticipantsDetails={cartParticipantsDetails}
                isFromCheckOutModal={isFromCheckOutModal}
                customer_id={customer_id}
                productInitialValues={productInfo}
                isCustomWidget={isCustomWidget}
              />
            )
          }
          return (
            <ProductBuyModal
              show={show}
              selectedProduct={selectedProduct?.id || (productDetails.product_id as string)}
              selectedProductData={selectedProduct}
              pagePreview={productDetails?.product_description_view || "default_view"}
              goToBackStep={goToBackStep}
              closeModal={handleModalClose}
              onSuccess={(cartId: string) => {
                if (isFromCheckOutModal) {
                  refetchCartData()
                  handleModalClose()
                } else {
                  setShowCheckOutModal(true)
                  setcartId(cartId)
                }
              }}
              handleHomeButtonClick={handleModalClose}
              isIntegratedPage={isIntegratedPage}
              setCurrentStepTobacktStep={setCurrentStep}
              currentStepToBackStep={currentStep}
              domainDetails={domainDetails}
              productInitialValues={productInfo}
              cartId={cart_Id}
              updateCart={Boolean(cart_Id)}
              cartItemDetails={cartItemDetails}
              cartParticipantsDetails={cartParticipantsDetails}
              isFromCheckOutModal={isFromCheckOutModal}
              customer_id={customer_id}
              isSelectedDate={isSelectedDate}
              isCustomWidget={isCustomWidget}
              refetchParticipantsDetails={refetchParticipantsDetails}
              orderSummary={orderSummary}
              isFromPagePreview={isFromPagePreview}
              hideStepper={hideStepper}
              lastStepCount={lastStepCount}
              onProductDetailsPreviewSubmit={onProductDetailsPreviewSubmit}
              hideQuantityEdit={hideQuantityEdit}
              hideCustomerDetails={hideCustomerDetails}
              productDetails={productDetails}
            />
          )
        }
      default:
        return null
    }
  }

  return (
    <>
      <Modal
        className={"widget-theme-wrapper"}
        show={show}
        onClose={() => handleModalClose(true)}
        size={"6xl"}
        theme={{
          content: {
            base: `relative w-full h-auto rounded-lg max-h-[92vh] overflow-hidden mx-2 !max-w-[1180px]`,
          },
        }}
      >
        <Modal.Header
          className={"sm:px-6 px-4"}
          theme={{
            title: "w-full text16Medium text-neutral-900 dark:text-white",
          }}
        >
          <div className={`${!isMobile ? "flex items-center justify-between" : ""} w-full`}>
            <div className={"flex items-center"}>
              {domainDetails?.logo ? (
                <div className={"pr-2 mr-2 border-r border-neutral-300"}>
                  {domainDetails?.logo !== "" ? (
                    <CustomImage
                      src={domainDetails?.logo}
                      // className={'overflow-hidden rounded-md mr-2.5'}
                      className={"w-auto max-h-9"}
                      height={40}
                      width={80}
                      fill={false}
                    />
                  ) : null}
                </div>
              ) : null}
              {currentStep !== 1 && (
                <LeftSmallIcon
                  width={18}
                  height={18}
                  className={"mr-2 cursor-pointer text-primary-600"}
                  onClick={handleBackButton}
                />
              )}
              {isLoading || isCategoryListWithProductsLoading ? (
                <div
                  className={"w-48 h-3 bg-gray-200 rounded-full animate-pulse dark:bg-gray-700"}
                />
              ) : (
                <>
                  <div className={"text18Medium"}>{modalHeading}</div>
                </>
              )}
            </div>
            {isIntegratedPage && !hideCustomerDetails && (
              <CustomerLogin isIntegratedPage={isIntegratedPage} />
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>
            {isLoading ? (
              <div className={"flex items-center justify-center min-h-[200px]"}>
                <Spinner />
              </div>
            ) : (
              <div>
                {isProductInfoLoader ? (
                  <div className={"flex items-center justify-center min-h-[200px]"}>
                    <Spinner />
                  </div>
                ) : (
                  <>{getModalBody()}</>
                )}
              </div>
            )}
          </div>
          <div
            className={`flex flex-1 flex-row items-center justify-between border-t border-neutral-200 bg-whiteBlack-100 sm:px-6 sm:py-4 px-4 py-3`}
          >
            <div className={"flex items-center justify-between w-full"}>
              <div className={"flex flex-col"}>
                <div className={"paragraph13 text-neutral-1000"}>
                  Powered by{" "}
                  <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                    <span className={"text-[#2F69FE] text-Medium"}>{`${widgetsDomainName}`}</span>
                  </a>
                </div>
                {domainDetails && domainDetails?.currency_iso3 ? (
                  <div
                    className={"text12Medium text-neutral-600"}
                  >{`All prices are in ${domainDetails?.currency_iso3}`}</div>
                ) : (
                  ""
                )}
              </div>
              <div className={"mr-2 w-fit"}>
                <a href={`https://www${domainPlaceholder}`} target={"_blank"} rel={"noreferrer"}>
                  <BookingSwLogo />
                </a>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {showCheckoutModal && (
        <CheckoutModal
          show={showCheckoutModal}
          handleModalClose={() => {
            setShowCheckOutModal(false)
            handleModalClose(true)
          }}
          goToBackStep={goToBackStep}
          cartId={cartId}
          onsucess={(orderDataResponse: GetOrderDetailsResponseType | undefined) => {
            setShowCheckOutModal(false)
            handleModalClose()
            setOrderConfirmModal(true)
            if (orderDataResponse) {
              setOrderData(orderDataResponse)
            }
          }}
          isIntegratedPage={isIntegratedPage}
          domainDetails={domainDetails}
          hideCustomerDetails={hideCustomerDetails}
        />
      )}
      {orderData && orderConfirmModal && (
        <OrderConfirmModal
          orderData={orderData}
          show={orderConfirmModal}
          setShow={setOrderConfirmModal}
          handleModalClose={() => {
            handleClose(true)
          }}
        />
      )}
    </>
  )
}

export default SelectProductModal
